"use client";

import { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import { useAuth } from "../contexts/AuthContext";
import { createUserWithEmailAndPassword } from "firebase/auth";
import { auth, database, storage } from "../lib/firebase";
import { ref, set, get } from "firebase/database";
import {
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
} from "firebase/storage";
import {
  Upload,
  Eye,
  EyeOff,
  CheckCircle,
  Calendar,
  Trophy,
  Users,
} from "lucide-react";
import { motion } from "framer-motion";
import Image from "next/image";
import PrimaryLoader from "../components/PrimaryLoader";

const SignUpPage = () => {
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
    acceptTerms: false,
  });
  const [submissionType, setSubmissionType] = useState("");
  const [submissionFile, setSubmissionFile] = useState(null);
  const [isCombinedSlip, setIsCombinedSlip] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSignedUp, setIsSignedUp] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [membershipVerified, setMembershipVerified] = useState(false);
  const [showMembershipModal, setShowMembershipModal] = useState(false);
  const [membershipModalType, setMembershipModalType] = useState(""); // "not_found" or "inactive"
  const [currentTime, setCurrentTime] = useState(new Date());
  const [userRecord, setUserRecord] = useState(null);
  const [userBookings, setUserBookings] = useState([]);
  const [userStats, setUserStats] = useState(null);
  const [shouldShowApprovalNotice, setShouldShowApprovalNotice] =
    useState(false);
  const [isUpdatingSubmission, setIsUpdatingSubmission] = useState(false);
  const [submissionUpdateError, setSubmissionUpdateError] = useState("");

  const router = useRouter();
  const { user, isApproved } = useAuth();

  // Update timer every second
  useEffect(() => {
    if (isSignedUp) {
      const timer = setInterval(() => {
        setCurrentTime(new Date());
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [isSignedUp]);

  // Fetch user record to determine if approval notice should be shown
  useEffect(() => {
    const fetchUserRecord = async () => {
      if (user?.uid) {
        try {
          const userRef = ref(database, `users/${user.uid}`);
          const snapshot = await get(userRef);

          if (snapshot.exists()) {
            const userData = snapshot.val();
            console.log("userData", userData);
            setUserRecord(userData);
            setUserStats(userData.initialStats || null);

            // Show approval notice only if user is not approved OR not active
            const isApproved = userData.approved === true;
            const isActive = userData.verificationStatus === "active";
            if (isActive && isApproved) {
              router.push("/welcome");
            }
            setShouldShowApprovalNotice(!isApproved || !isActive);
          } else {
            // If no user record exists, show the approval notice
            setShouldShowApprovalNotice(true);
          }

          // Fetch user bookings
          const bookingsRef = ref(database, "bookings");
          const bookingsSnapshot = await get(bookingsRef);
          if (bookingsSnapshot.exists()) {
            const bookingsData = bookingsSnapshot.val();
            const userBookingsArray = Object.values(bookingsData).filter(
              (booking) => booking.userId === user.uid
            );
            setUserBookings(userBookingsArray);
          }
        } catch (error) {
          console.error("Error fetching user record:", error);
          // On error, show the approval notice as a safe default
          setShouldShowApprovalNotice(true);
        }
      }
    };

    if (isSignedUp && user) {
      fetchUserRecord();
    }
  }, [isSignedUp, user]);

  // Redirect authenticated users only if they are approved and active
  useEffect(() => {
    console.log("user", user);
    if (user && isApproved) {
      router.push("/welcome");
    }
  }, [user, isApproved, router]);

  // Set signed up state for logged-in but unapproved users
  useEffect(() => {
    if (user && !isApproved) {
      setIsSignedUp(true);
    }
  }, [user, isApproved]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type and size
      const allowedTypes = [
        "image/jpeg",
        "image/png",
        "image/jpg",
        "application/pdf",
      ];
      const maxSize = 10 * 1024 * 1024; // 10MB

      if (!allowedTypes.includes(file.type)) {
        setError("Please upload a valid image (JPG, PNG) or PDF file");
        return;
      }

      if (file.size > maxSize) {
        setError("File size must be less than 10MB");
        return;
      }

      setSubmissionFile(file);
      setError("");
    }
  };

  const getSubmissionTypeLabel = () => {
    switch (submissionType) {
      case "slip1":
        return "Slip 1";
      case "slip2":
        return "Slip 2";
      case "registration":
        return "Registration Document";
      default:
        return "submission document";
    }
  };

  const getStoragePath = (userId, fileName) => {
    switch (submissionType) {
      case "slip1":
        return `slips1/${userId}/${fileName}`;
      case "slip2":
        return `slips2/${userId}/${fileName}`;
      case "registration":
        return `registrations/${userId}/${fileName}`;
      default:
        return `submissions/${userId}/${fileName}`;
    }
  };

  // Member search function to verify membership
  const searchMember = async (email, fullName) => {
    if (!email.trim() || !fullName.trim()) return false;

    setIsSearching(true);
    try {
      // Split full name to get first and last names
      const nameParts = fullName.trim().split(" ");
      const firstName = nameParts[0];

      const lastName = nameParts.slice(1).join(" ");

      // Search by email first, then by name if email doesn't work
      // Clean and sanitize the email to remove any unwanted characters
      const cleanEmail = email.trim().replace(/[^\w@.-]/g, "");
      let queryParam = `email=${encodeURIComponent(cleanEmail)}`;
      console.log(`Searching for member with email: ${cleanEmail}`);
      const response = await fetch(`/api/search-members?${queryParam}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        // If we get a 500 error or any error, show not found modal
        setMembershipModalType("not_found");
        setShowMembershipModal(true);
        return false;
      }

      const result = await response.json();
      return checkMembershipStatus(result);
    } catch (error) {
      console.error("Error searching for member:", error);
      // Show not found modal for any API errors
      setMembershipModalType("not_found");
      setShowMembershipModal(true);
      return false;
    } finally {
      setIsSearching(false);
    }
  };

  const checkMembershipStatus = (result) => {
    if (!result || result.length === 0) {
      setMembershipModalType("not_found");
      setShowMembershipModal(true);
      return false;
    }

    // Filter to only show Active members
    const activeMembers = result.filter(
      (member) => member.contract?.status === "Active"
    );

    if (activeMembers.length > 0) {
      setMembershipVerified(true);
      return true;
    } else {
      setMembershipModalType("inactive");
      setShowMembershipModal(true);
      return false;
    }
  };

  // Function to update submission on the success page
  const handleSubmissionUpdate = async () => {
    if (!submissionType || !submissionFile || !user?.uid) {
      setSubmissionUpdateError(
        "Please select a submission type and upload a file"
      );
      return;
    }

    setIsUpdatingSubmission(true);
    setSubmissionUpdateError("");

    try {
      // Upload submission file to appropriate folder
      const storagePath = getStoragePath(user.uid, submissionFile.name);
      const fileRef = storageRef(storage, storagePath);
      const snapshot = await uploadBytes(fileRef, submissionFile);
      const downloadURL = await getDownloadURL(snapshot.ref);

      // Prepare submission type flags - reset all flags and set only the new one
      const submissionFlags = {
        hasSlip1: submissionType === "slip1",
        hasSlip2:
          submissionType === "slip2" ||
          (submissionType === "slip1" && isCombinedSlip),
        hasRegistrationDocument: submissionType === "registration",
      };

      // Get current user data and merge with submission data
      const userRef = ref(database, `users/${user.uid}`);
      const snapshot2 = await get(userRef);
      const currentData = snapshot2.val() || {};

      // If user was previously rejected, reset their status to pending
      const statusUpdate =
        currentData.verificationStatus === "rejected"
          ? { verificationStatus: "pending" }
          : {};

      // For rejected users replacing submission, clear previous submission flags
      const clearPreviousFlags =
        currentData.verificationStatus === "rejected"
          ? {
              hasSlip1: false,
              hasSlip2: false,
              hasRegistrationDocument: false,
            }
          : {};

      const timestamp = new Date().toISOString();

      // Update user data in database with submission info
      const updatedData = {
        submissionType: submissionType,
        submissionFileURL: downloadURL,
        submissionFileName: submissionFile.name,
        ...clearPreviousFlags,
        ...submissionFlags,
        ...statusUpdate,
        submissionUpdatedAt: timestamp,
      };

      // Add specific document fields based on submission type
      if (submissionType === "slip1") {
        updatedData.slip1FileURL = downloadURL;
        updatedData.slip1FileName = submissionFile.name;
        updatedData.slip1UploadedAt = timestamp;
        if (isCombinedSlip) {
          updatedData.slip2FileURL = downloadURL;
          updatedData.slip2FileName = submissionFile.name;
          updatedData.slip2UploadedAt = timestamp;
        }
      } else if (submissionType === "slip2") {
        updatedData.slip2FileURL = downloadURL;
        updatedData.slip2FileName = submissionFile.name;
        updatedData.slip2UploadedAt = timestamp;
      } else if (submissionType === "registration") {
        updatedData.registrationFileURL = downloadURL;
        updatedData.registrationFileName = submissionFile.name;
        updatedData.registrationUploadedAt = timestamp;
      }

      await set(userRef, { ...currentData, ...updatedData });

      // Send email notifications
      try {
        // Send confirmation email to user
        await fetch(
          "https://competitions.bodyactiongym.co.za/api/send-proof-received-email",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              email: currentData.email || user.email,
              name: currentData.fullName || "Member",
              documentType: submissionType,
            }),
          }
        );

        // Send notification email to admin
        await fetch(
          "https://competitions.bodyactiongym.co.za/api/send-admin-notification",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              registrantName: currentData.fullName || "Unknown",
              registrantEmail: currentData.email || user.email,
              documentType: submissionType,
              submissionDate: new Date().toISOString(),
            }),
          }
        );

        console.log("Email notifications sent successfully");
      } catch (emailError) {
        console.error("Error sending email notifications:", emailError);
        // Don't fail the submission if emails fail
      }

      // Reset form
      setSubmissionType("");
      setSubmissionFile(null);

      // Show appropriate success message
      const successMessage =
        currentData.verificationStatus === "rejected"
          ? "Document resubmitted successfully! Your verification status has been reset to pending."
          : "Submission updated successfully!";

      alert(successMessage);

      // Refresh user record to update UI
      try {
        const userRef = ref(database, `users/${user.uid}`);
        const snapshot = await get(userRef);
        if (snapshot.exists()) {
          const userData = snapshot.val();
          setUserRecord(userData);

          // Update approval notice status
          const isApproved = userData.approved === true;
          const isActive = userData.verificationStatus === "active";
          setShouldShowApprovalNotice(!isApproved || !isActive);
        }
      } catch (refreshError) {
        console.error("Error refreshing user record:", refreshError);
      }
    } catch (error) {
      console.error("Error updating submission:", error);
      setSubmissionUpdateError(
        "Failed to update submission. Please try again."
      );
    } finally {
      setIsUpdatingSubmission(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    // Validation
    if (formData.password !== formData.confirmPassword) {
      setError("Passwords do not match");
      setLoading(false);
      return;
    }

    if (formData.password.length < 6) {
      setError("Password must be at least 6 characters");
      setLoading(false);
      return;
    }

    if (!formData.acceptTerms) {
      setError("Please accept the Terms & Conditions");
      setLoading(false);
      return;
    }

    // Verify membership before proceeding
    if (!membershipVerified) {
      const isActiveMember = await searchMember(
        formData.email,
        formData.fullName
      );
      if (!isActiveMember) {
        setLoading(false);
        return; // Stop here if not an active member
      }
    }

    try {
      // Create user account
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        formData.email,
        formData.password
      );
      const user = userCredential.user;

      // Save user data to database (without submission info)
      const userData = {
        uid: user.uid,
        fullName: formData.fullName,
        email: formData.email,
        phone: formData.phone,
        createdAt: new Date().toISOString(),
        approved: false,
        verificationStatus: "pending",
      };

      await set(ref(database, `users/${user.uid}`), userData);

      // Send welcome email to the user
      try {
        await fetch(
          "https://competitions.bodyactiongym.co.za/api/send-welcome-email",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              email: formData.email,
              fullName: formData.fullName,
            }),
          }
        );
        console.log("Welcome email sent successfully");
      } catch (emailError) {
        console.error("Error sending welcome email:", emailError);
        // Don't fail the registration if email fails
      }

      setIsSignedUp(true);
    } catch (error) {
      console.error("Error creating account:", error);
      setError(error.message || "Failed to create account. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Animation variants
  const pageVariants = {
    initial: { opacity: 0, y: 20 },
    in: { opacity: 1, y: 0 },
    out: { opacity: 0, y: -20 },
  };

  const pageTransition = {
    type: "tween",
    ease: "anticipate",
    duration: 0.4,
  };

  const formVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.5,
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  if (isSignedUp) {
    // Calculate time until July 25, 2025
    const launchDate = new Date("2025-07-25T00:00:00");
    const timeLeft = launchDate - currentTime;

    const days = Math.max(0, Math.floor(timeLeft / (1000 * 60 * 60 * 24)));
    const hours = Math.max(
      0,
      Math.floor((timeLeft % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
    );
    const minutes = Math.max(
      0,
      Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60))
    );
    const seconds = Math.max(0, Math.floor((timeLeft % (1000 * 60)) / 1000));

    return (
      <motion.div
        className="min-h-screen flex items-center justify-center bg-background py-12 px-4"
        initial="initial"
        animate="in"
        exit="out"
        variants={pageVariants}
        transition={pageTransition}
      >
        <div className="max-w-2xl w-full text-center">
          <motion.div
            className="bg-dark-gray rounded-lg p-8 shadow-2xl"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.4 }}
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.4, duration: 0.5, type: "spring" }}
            >
              <CheckCircle className="w-16 h-16 text-primary mx-auto mb-4" />
            </motion.div>
            <h2 className="text-2xl font-bold mb-4 text-white">
              Welcome to The Inner Circle Club!
            </h2>

            {/* Account Status Notice - Only show if user needs approval */}
            {userRecord &&
              (userRecord.hasSlip1 ||
                userRecord.hasSlip2 ||
                userRecord.hasRegistrationDocument) &&
              userRecord.approved === false && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6, duration: 0.4 }}
                  className="bg-orange-500/10 border border-orange-500/20 rounded-lg p-4 mb-6"
                >
                  <p className="text-orange-400 text-sm">
                    <strong>Account Status:</strong>
                    <br />
                    You&apos;re seeing this page because your account is
                    awaiting approval from the Body Action Gym team. Once
                    approved, you&apos;ll gain full access to the Inner Circle
                    Club platform.
                  </p>
                </motion.div>
              )}

            {/* Red warning for users with no submissions AND not approved */}
            {userRecord &&
              !userRecord.hasSlip1 &&
              !userRecord.hasSlip2 &&
              !userRecord.hasRegistrationDocument &&
              userRecord.approved === false &&
              userRecord.verificationStatus !== "rejected" && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5, duration: 0.4 }}
                  className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 mb-6"
                >
                  <p className="text-red-400 text-sm">
                    <strong>⚠️ Action Required:</strong>
                    <br />
                    Please submit one of the required documents below to
                    complete your registration.
                  </p>
                </motion.div>
              )}

            {/* Rejection notice for users with rejected verification status */}
            {userRecord && userRecord.verificationStatus === "rejected" && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.4 }}
                className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 mb-6"
              >
                <p className="text-red-400 text-sm">
                  <strong>❌ Document Rejected:</strong>
                  <br />
                  Your submitted document has been rejected. Please review the
                  requirements and submit a new document below to complete your
                  registration.
                </p>
              </motion.div>
            )}

            {/* Success message for approved users */}
            {!shouldShowApprovalNotice && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6, duration: 0.4 }}
                className="bg-green-500/10 border border-green-500/20 rounded-lg p-4 mb-6"
              >
                <p className="text-green-400 text-sm">
                  <strong>Account Status:</strong>
                  <br />
                  Your account has been approved! You have full access to the
                  Inner Circle Club platform.
                </p>
              </motion.div>
            )}

            {/* Submission Management - Show if user has no submissions OR verification was rejected */}
            {userRecord &&
              ((!userRecord.hasSlip1 &&
                !userRecord.hasSlip2 &&
                !userRecord.hasRegistrationDocument) ||
                userRecord.verificationStatus === "rejected") && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8, duration: 0.4 }}
                  className="bg-background rounded-lg border border-red-400 p-6 mb-6 text-left"
                >
                  <h3 className="text-white font-bold text-lg mb-4 text-center">
                    {userRecord.verificationStatus === "rejected"
                      ? "Resubmit Your Document"
                      : "Submit Your Document"}
                  </h3>

                  {submissionUpdateError && (
                    <motion.div
                      className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg text-sm"
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      {submissionUpdateError}
                    </motion.div>
                  )}

                  {/* Submission Type Selection */}
                  <div className="mb-4">
                    <label className="block text-sm font-semibold mb-2 text-white">
                      Submission Type
                    </label>
                    <select
                      value={submissionType}
                      onChange={(e) => {
                        setSubmissionType(e.target.value);
                        setIsCombinedSlip(false); // Reset combined slip when type changes
                      }}
                      className="w-full px-4 py-3 border border-gray-600 bg-dark-gray text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      disabled={isUpdatingSubmission}
                    >
                      <option value="">Select submission type</option>
                      <option value="slip1">
                        Body Action Sportsgear NPL purchase proof of purchase 1
                      </option>
                      <option value="slip2">
                        Body Action Sportsgear NPL purchase proof of purchase 2
                      </option>
                      <option value="registration">
                        NPL Body Rev Challenge proof of registration
                      </option>
                    </select>
                  </div>

                  {/* Combined Slip Checkbox for slip1 */}
                  {submissionType === "slip1" && (
                    <div className="mb-4">
                      <label className="flex items-center space-x-3 text-white">
                        <input
                          type="checkbox"
                          checked={isCombinedSlip}
                          onChange={(e) => setIsCombinedSlip(e.target.checked)}
                          className="w-4 h-4 text-primary focus:ring-primary border-gray-600 rounded bg-background"
                          disabled={isUpdatingSubmission}
                        />
                        <span className="text-sm">
                          This slip contains proof of purchase for both bundles
                        </span>
                      </label>
                      <p className="text-xs text-gray-400 mt-1">
                        Check this if your single slip shows purchases for both
                        NPL bundles
                      </p>
                    </div>
                  )}

                  {/* File Upload */}
                  {submissionType && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      transition={{ duration: 0.4 }}
                      className="mb-4"
                    >
                      <label className="block text-sm font-semibold mb-2 text-white">
                        {getSubmissionTypeLabel()}
                      </label>
                      <div className="border-2 border-dashed border-gray-600 rounded-lg p-4 text-center hover:border-primary transition-colors">
                        <input
                          type="file"
                          onChange={handleFileChange}
                          accept="image/*,.pdf"
                          className="hidden"
                          id="fileUploadSuccess"
                          disabled={isUpdatingSubmission}
                        />
                        <label
                          htmlFor="fileUploadSuccess"
                          className={`cursor-pointer ${
                            isUpdatingSubmission ? "opacity-50" : ""
                          }`}
                        >
                          <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                          <p className="text-gray-300 text-sm mb-1">
                            {submissionFile
                              ? submissionFile.name
                              : `Upload your ${getSubmissionTypeLabel()}`}
                          </p>
                          <p className="text-gray-500 text-xs">
                            JPG, PNG, PDF (Max 10MB)
                          </p>
                        </label>
                      </div>
                    </motion.div>
                  )}

                  {/* Submit Button */}
                  {submissionType && submissionFile && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      transition={{ duration: 0.4 }}
                      className="mb-4"
                    >
                      <button
                        onClick={handleSubmissionUpdate}
                        disabled={isUpdatingSubmission}
                        className="w-full bg-primary hover:bg-primary-dark text-black font-semibold py-3 px-6 rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                      >
                        {isUpdatingSubmission ? (
                          <div className="flex items-center">
                            <PrimaryLoader size="small" className="mr-3" />
                            Uploading...
                          </div>
                        ) : (
                          <>
                            <Upload className="w-5 h-5 mr-2" />
                            {userRecord?.verificationStatus === "rejected"
                              ? "Replace Document"
                              : "Submit Document"}
                          </>
                        )}
                      </button>
                    </motion.div>
                  )}
                </motion.div>
              )}

            {/* Show submission status if user already has documents and not rejected */}
            {userRecord &&
              (userRecord.hasSlip1 ||
                userRecord.hasSlip2 ||
                userRecord.hasRegistrationDocument) &&
              userRecord.verificationStatus !== "rejected" && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8, duration: 0.4 }}
                  className="bg-green-500/10 border border-green-500/20 rounded-lg p-6 mb-6"
                >
                  <h3 className="text-green-400 font-bold text-lg mb-3 text-center">
                    Submission Complete ✓
                  </h3>
                  <div className="text-center text-green-300">
                    <p className="mb-2">You have successfully submitted:</p>
                    {userRecord.hasSlip1 && (
                      <p className="text-sm">
                        • Body Action Sportsgear NPL purchase slip one
                      </p>
                    )}
                    {userRecord.hasSlip2 && (
                      <p className="text-sm">
                        • Body Action Sportsgear NPL purchase slip two
                      </p>
                    )}
                    {userRecord.hasRegistrationDocument && (
                      <p className="text-sm">
                        • NPL Body Rev Challenge proof of registration
                      </p>
                    )}
                  </div>
                </motion.div>
              )}

            {/* Show current submission status for rejected users */}
            {userRecord &&
              (userRecord.hasSlip1 ||
                userRecord.hasSlip2 ||
                userRecord.hasRegistrationDocument) &&
              userRecord.verificationStatus === "rejected" && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8, duration: 0.4 }}
                  className="bg-orange-500/10 border border-orange-500/20 rounded-lg p-6 mb-6"
                >
                  <h3 className="text-orange-400 font-bold text-lg mb-3 text-center">
                    Current Submission (Rejected)
                  </h3>
                  <div className="text-center text-orange-300">
                    <p className="mb-2">Previously submitted document:</p>
                    {userRecord.hasSlip1 && (
                      <p className="text-sm">
                        • Body Action Sportsgear NPL purchase slip one
                      </p>
                    )}
                    {userRecord.hasSlip2 && (
                      <p className="text-sm">
                        • Body Action Sportsgear NPL purchase slip two
                      </p>
                    )}
                    {userRecord.hasRegistrationDocument && (
                      <p className="text-sm">
                        • NPL Body Rev Challenge proof of registration
                      </p>
                    )}
                    <p className="text-xs text-orange-400 mt-2">
                      Submit a new document below to replace this submission.
                    </p>
                  </div>
                </motion.div>
              )}

            {/* Countdown Timer */}
            <div className="bg-primary/10 border border-primary/20 rounded-lg p-6 mb-6">
              <h3 className="text-primary font-bold text-lg mb-4">
                Platform Launch Countdown
              </h3>
              {timeLeft > 0 ? (
                <div className="grid grid-cols-4 gap-4 mb-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-white">{days}</div>
                    <div className="text-gray-400 text-xs">Days</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-white">{hours}</div>
                    <div className="text-gray-400 text-xs">Hours</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-white">
                      {minutes}
                    </div>
                    <div className="text-gray-400 text-xs">Minutes</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-white">
                      {seconds}
                    </div>
                    <div className="text-gray-400 text-xs">Seconds</div>
                  </div>
                </div>
              ) : (
                <div className="text-primary text-xl font-bold mb-4">
                  Platform is now LIVE! 🎉
                </div>
              )}
            </div>

            {/* Journey Steps */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7, duration: 0.4 }}
              className="mb-6"
            >
              <h3 className="text-white font-bold text-lg mb-4 text-center">
                Your Inner Circle Journey
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {(() => {
                  // Define journey steps with dynamic completion status
                  const isUserApproved = userRecord?.approved === true;
                  const hasBooking = userBookings.length > 0;
                  const hasStatsUploaded = userStats !== null;
                  const hasConfirmedBooking = userBookings.some(
                    (booking) => booking.status === "confirmed"
                  );

                  const journeySteps = [
                    {
                      step: 1,
                      title: "Registration",
                      description: "Sign up and get verified",
                      icon: "✓",
                      completed: isUserApproved,
                    },
                    {
                      step: 2,
                      title: "Welcome",
                      description: "Access your Inner Circle portal",
                      icon: "✓",
                      completed: false,
                    },
                    {
                      step: 3,
                      title: "Consultation",
                      description: "Book your assessment session",
                      icon: <Calendar size={16} />,
                      completed: hasStatsUploaded, // Completed when stats are uploaded by Jamayan
                      tentative: hasConfirmedBooking && !hasStatsUploaded, // Orange when booked but no stats yet
                    },
                    {
                      step: 4,
                      title: "Track Progress",
                      description: "Upload weekly health stats",
                      icon: <Upload size={16} />,
                      completed: false,
                    },
                    {
                      step: 5,
                      title: "Compete",
                      description: "See your rank on leaderboard",
                      icon: <Trophy size={16} />,
                      completed: false,
                    },
                    {
                      step: 6,
                      title: "Community",
                      description: "Connect with fellow members",
                      icon: <Users size={16} />,
                      completed: false,
                    },
                  ];

                  return journeySteps.map((step, index) => (
                    <motion.div
                      key={step.step}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.8 + index * 0.1, duration: 0.4 }}
                      className={`bg-background rounded-lg p-4 shadow-lg border-2 transition-all ${
                        step.completed
                          ? "border-primary bg-primary/5"
                          : step.tentative
                          ? "border-orange-500 bg-orange-500/5"
                          : "border-gray-600"
                      }`}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div
                          className={`w-8 h-8 rounded-full flex items-center justify-center text-sm ${
                            step.completed
                              ? "bg-primary text-black"
                              : step.tentative
                              ? "bg-orange-500 text-white"
                              : "bg-gray-600 text-white"
                          }`}
                        >
                          {typeof step.icon === "string"
                            ? step.icon
                            : step.icon}
                        </div>
                        <span className="text-gray-400 text-xs">
                          Step {step.step}
                        </span>
                      </div>
                      <h4 className="text-white font-semibold text-sm mb-1">
                        {step.title}
                      </h4>
                      <p className="text-gray-300 text-xs">
                        {step.description}
                      </p>
                      {step.tentative && (
                        <p className="text-orange-400 text-xs mt-1">
                          ⏳ Awaiting consultation completion
                        </p>
                      )}
                    </motion.div>
                  ));
                })()}
              </div>
            </motion.div>

            {/* Action Buttons */}
            <div className="space-y-4">
              {/* WhatsApp Group Button */}
              <motion.a
                href="https://chat.whatsapp.com/FM0RecWIKQJFXVAhSzeQxi?mode=ac_t"
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                🟢 Join The Inner Circle Club WhatsApp community group
              </motion.a>

              {/* Q&A Form Button */}
              <motion.button
                onClick={() => router.push("/qa-form")}
                className="w-full bg-primary hover:bg-primary-dark text-black font-semibold py-3 px-6 rounded-lg transition-colors duration-200"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                📝 Share Your Questions & Goals (Q&A Form)
              </motion.button>

              {/* Terms Button */}
              <motion.button
                onClick={() => router.push("/terms")}
                className="w-full bg-gray-600 hover:bg-gray-500 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Review Terms & Conditions
              </motion.button>
            </div>

            {/* Next Steps */}
            <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 mt-6">
              <p className="text-primary text-sm">
                <strong>Next Steps:</strong>
                <br />
                • You&apos;ll receive an email confirmation once approved
                <br />
                • Join The Inner Circle Club WhatsApp community group to stay
                updated
                <br />
                • Fill out our Q&A form to help us create better content for the
                community
                <br />
                • Keep an eye on your inbox for updates
                <br />• Platform features unlock on July 25, 2025
              </p>
            </div>
          </motion.div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      className="min-h-screen bg-background py-12 px-4 sm:px-6 lg:px-8"
      initial="initial"
      animate="in"
      exit="out"
      variants={pageVariants}
      transition={pageTransition}
    >
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <motion.div
          className="text-center mb-8"
          variants={itemVariants}
          initial="hidden"
          animate="visible"
          transition={{ delay: 0.1 }}
        >
          <div className="flex justify-center mb-6">
            <motion.div
              whileHover={{ scale: 1.05 }}
              transition={{ duration: 0.2 }}
            >
              <Image
                src="/BAG-logo.svg"
                alt="Body Action Gym"
                width={120}
                height={60}
                className=""
              />
            </motion.div>
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">
            Join the Inner Circle Club
          </h1>
          <p className="text-gray-300">
            Exclusive access for NPL Body Rev Challenge participants
          </p>
        </motion.div>

        {/* Sign Up Form */}
        <motion.div
          className="bg-dark-gray rounded-lg shadow-2xl p-8"
          variants={formVariants}
          initial="hidden"
          animate="visible"
        >
          {error && (
            <motion.div
              className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              {error}
            </motion.div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Full Name */}
            <motion.div variants={itemVariants}>
              <label
                htmlFor="fullName"
                className="block text-sm font-semibold mb-2 text-white"
              >
                Full Name *
              </label>
              <motion.input
                id="fullName"
                name="fullName"
                type="text"
                required
                value={formData.fullName}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-600 bg-background text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200"
                placeholder="Enter your full name"
                whileFocus={{ scale: 1.02 }}
              />
            </motion.div>

            {/* Email */}
            <motion.div variants={itemVariants}>
              <label
                htmlFor="email"
                className="block text-sm font-semibold mb-2 text-white"
              >
                Email Address *
              </label>
              <motion.input
                id="email"
                name="email"
                type="text"
                required
                value={formData.email}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-600 bg-background text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200"
                placeholder="your.email@example.com"
                whileFocus={{ scale: 1.02 }}
              />
            </motion.div>

            {/* Phone */}
            <motion.div variants={itemVariants}>
              <label
                htmlFor="phone"
                className="block text-sm font-semibold mb-2 text-white"
              >
                Phone Number *
              </label>
              <input
                id="phone"
                name="phone"
                type="tel"
                required
                value={formData.phone}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-600 bg-background text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="+27 123 456 789"
              />
            </motion.div>

            {/* Password */}
            <div>
              <label
                htmlFor="password"
                className="block text-sm font-semibold mb-2 text-white"
              >
                Password *
              </label>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  required
                  value={formData.password}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 pr-12 border border-gray-600 bg-background text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            {/* Confirm Password */}
            <div>
              <label
                htmlFor="confirmPassword"
                className="block text-sm font-semibold mb-2 text-white"
              >
                Confirm Password *
              </label>
              <div className="relative">
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  required
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 pr-12 border border-gray-600 bg-background text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
                >
                  {showConfirmPassword ? (
                    <EyeOff size={20} />
                  ) : (
                    <Eye size={20} />
                  )}
                </button>
              </div>
            </div>

            {/* Terms & Conditions */}
            <div className="flex items-start space-x-3">
              <input
                id="acceptTerms"
                name="acceptTerms"
                type="checkbox"
                checked={formData.acceptTerms}
                onChange={handleInputChange}
                className="mt-1 h-4 w-4 text-primary focus:ring-primary border-gray-600 rounded bg-background"
              />
              <label htmlFor="acceptTerms" className="text-sm text-gray-300">
                I agree to the{" "}
                <button
                  type="button"
                  onClick={() => window.open("/terms", "_blank")}
                  className="text-primary hover:underline"
                >
                  Terms & Conditions
                </button>{" "}
                and Privacy Policy *
              </label>
            </div>

            {/* Submit Button */}
            <motion.button
              type="submit"
              disabled={loading || isSearching}
              className="w-full bg-primary hover:bg-primary-dark text-black font-semibold py-3 px-6 rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              whileHover={{ scale: loading || isSearching ? 1 : 1.02 }}
              whileTap={{ scale: loading || isSearching ? 1 : 0.98 }}
            >
              {isSearching ? (
                <div className="flex items-center">
                  <PrimaryLoader size="small" className="mr-3" />
                  Verifying Membership...
                </div>
              ) : loading ? (
                <div className="flex items-center">
                  <PrimaryLoader size="small" className="mr-3" />
                  Creating Account...
                </div>
              ) : membershipVerified ? (
                <>
                  <CheckCircle className="w-5 h-5 mr-2" />
                  Join Inner Circle Club
                </>
              ) : (
                "Verify Membership & Join"
              )}
            </motion.button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-gray-400 text-sm">
              Already have an account?{" "}
              <button
                onClick={() => router.push("/login")}
                className="text-primary hover:underline"
              >
                Sign in here
              </button>
            </p>
          </div>
        </motion.div>

        {/* Footer */}
        <motion.div
          className="mt-8 text-center text-gray-500 text-sm"
          variants={itemVariants}
          initial="hidden"
          animate="visible"
          transition={{ delay: 0.6 }}
        >
          <p>© 2025 Body Action Gym. All rights reserved.</p>
        </motion.div>
      </div>

      {/* Membership Verification Modal */}
      {showMembershipModal && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-dark-gray rounded-lg p-6 max-w-[35rem] w-full mx-4"
          >
            <div className="text-center">
              <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg
                  className="w-8 h-8 text-red-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </div>

              {membershipModalType === "not_found" ? (
                <>
                  <h3 className="text-xl font-bold text-white mb-2">
                    Not a Valid Body Action Gym Member
                  </h3>
                  <p className="text-gray-300 mb-4">
                    We couldn&apos;t find an active Body Action Gym membership
                    with the email address you provided.
                  </p>
                  <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 mb-6">
                    <p className="text-primary text-sm font-medium mb-2">
                      Please check:
                    </p>
                    <ul
                      className="text-primary text-sm text-left space-y-1 text-wrap: nowrap;
"
                    >
                      <li>
                        • You&apos;re using the same email address from your
                        Body Action Gym registration
                      </li>
                      <li>• Your BAG membership is currently active</li>
                    </ul>
                  </div>
                </>
              ) : (
                <>
                  <h3 className="text-xl font-bold text-white mb-2">
                    Inactive Membership
                  </h3>
                  <p className="text-gray-300 mb-6">
                    We found your membership but it&apos;s currently inactive.
                    Please ensure your Body Action Gym membership is active
                    before joining the Inner Circle Club.
                  </p>
                </>
              )}

              <div className="space-y-3">
                <p className="text-sm text-gray-400">
                  If you&apos;re sure your details are correct:
                </p>
                <ul className="text-sm text-gray-300 text-left space-y-1">
                  <li>• Contact Body Action Gym to verify your membership</li>
                  <li>
                    • Ensure your email address is updated on your profile at
                    reception
                  </li>
                </ul>
              </div>

              <div className="mt-6 space-y-3">
                <button
                  onClick={() => {
                    setShowMembershipModal(false);
                    setMembershipVerified(false);
                    setError("");
                  }}
                  className="w-full bg-primary hover:bg-primary-dark text-black font-semibold py-3 px-6 rounded-lg transition-colors duration-200"
                >
                  Update Details & Try Again
                </button>

                <a
                  href="mailto:info@bodyactiongym.co.za"
                  className="block w-full bg-gray-600 hover:bg-gray-500 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200 text-center"
                >
                  Contact BAG Support
                </a>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </motion.div>
  );
};

export default SignUpPage;
